import { FC, useCallback, useState } from "react";
import { Dialog, LinearProgress } from "@mui/material";
import { useEffectOnceWhen } from "rooks";
import Stack from "@mui/material/Stack";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getBillingExplorerCsvExplorerPossibleColumnsThunk } from "@/store/resource-explorer/thunks/csv-export/getBillingExplorerCsvExplorerPossibleColumnsThunk";
import { billingExplorerCsvPossibleColumnsLoadingSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/csv/possible-columns/billingExplorerCsvPossibleColumnsLoadingSelector";
import { CsvDownloadName } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/CsvDownloadName";
import { CsvDownloadDialogColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/CsvDownloadDialogColumns";
import { CsvSelectedPossibleColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/utils/types";
import { CsvDownloadDialogActions } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/CsvDownloadDialogActions";
import { resourceExplorerCsvFileNameSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/csv/resourceExplorerCsvFileNameSelector";
import { DialogTitleClosable } from "@/components/common/dialog-utils/DialogTitleClosable";
import { billingExplorerCsvPossibleColumnsFilteredFormatedDataSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/csv/possible-columns/billingExplorerCsvPossibleColumnsFilteredFormatedDataSelector";
import { resourceExplorerDataGridViewSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/resourceExplorerDataGridViewSelector";
import { getInitialSelectedColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/utils/getInitialSelectedColumns";

interface CsvDownloadDialogProps {
  open: boolean;
  onClose(): void;
}

export const CsvDownloadDialog: FC<CsvDownloadDialogProps> = ({
  open,
  onClose,
}) => {
  const [name, setName] = useState("");
  const [selectedColumns, setSelectedColumns] =
    useState<CsvSelectedPossibleColumns | null>(null);
  const [dimensions, setDimensions] = useState<string[]>([]);
  const [tagKeys, setTagKeys] = useState<string[]>([]);

  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    billingExplorerCsvPossibleColumnsLoadingSelector,
  );
  const data = useAppSelector(
    billingExplorerCsvPossibleColumnsFilteredFormatedDataSelector,
  );
  const gridView = useAppSelector(resourceExplorerDataGridViewSelector);
  const filename = useAppSelector(resourceExplorerCsvFileNameSelector);

  const selectedColumnsChangeHandler = useCallback(
    (data: Record<string, boolean>) => {
      setSelectedColumns((prev) => {
        return { ...prev, ...data };
      });
    },
    [],
  );

  const setAllColumnsChecked = useCallback(() => {
    if (!data) {
      return;
    }
    const initialSelectedColumns = getInitialSelectedColumns(data, gridView);

    setSelectedColumns(initialSelectedColumns);
  }, [data, gridView]);

  useEffectOnceWhen(() => {
    dispatch(getBillingExplorerCsvExplorerPossibleColumnsThunk());
  }, open);

  useEffectOnceWhen(() => {
    setName(filename);
  }, !!filename);

  useEffectOnceWhen(() => {
    setAllColumnsChecked();
  }, !!data);

  return (
    <Dialog open={open} onClose={onClose} fullWidth PaperProps={paperProps}>
      <DialogTitleClosable title="Explort CSV" onClose={onClose} />

      {loading && <LinearProgress />}

      <Stack
        p={2}
        spacing={2}
        overflow="hidden"
        borderTop={1}
        borderBottom={1}
        borderColor="grey.300"
      >
        <CsvDownloadName name={name} onChange={setName} />

        <CsvDownloadDialogColumns
          selectedColumns={selectedColumns}
          onSelectedColumnsChange={selectedColumnsChangeHandler}
          dimensions={dimensions}
          onDimensionChange={setDimensions}
          tagKeys={tagKeys}
          onTagKeyChange={setTagKeys}
        />
      </Stack>

      <CsvDownloadDialogActions
        name={name}
        selectedColumnKeys={selectedColumns}
        onClose={onClose}
        dimensions={dimensions}
        tagKeys={tagKeys}
      />
    </Dialog>
  );
};

const paperProps = {
  sx: { minWidth: 900 },
};
